import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Truck, Package, Car, Home, Wrench, Users, Building2, ChevronDown, LogOut, User, CreditCard, Baby, Sparkles, ChefHat, FileText, Briefcase, FolderOpen, Receipt, MapPin, Shield, Lock, Settings, ArrowLeftRight, Flame, Brain, Headphones, Snowflake, TreePine } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";



export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, loading, refreshUser } = useAuth();

  // Close mobile menu on route change or custom event
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const close = () => setIsOpen(false);
    window.addEventListener("close-mobile-menu", close);
    return () => window.removeEventListener("close-mobile-menu", close);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const services = [
    { href: "/cargo", label: "Cargo", icon: Truck, desc: "Large items & furniture" },
    { href: "/courier", label: "Courier", icon: Package, desc: "Package delivery" },
    { href: "/towing", label: "Towing", icon: Car, desc: "Vehicle towing" },
    { href: "/nanny", label: "Nanny", icon: Baby, desc: "Private childcare" },
    { href: "/cleaning", label: "Cleaning", icon: Sparkles, desc: "Home & office cleaning" },
    { href: "/private-chef", label: "Private Chef", icon: ChefHat, desc: "Personal chef for events" },
    { href: "/movers", label: "Movers", icon: Home, desc: "Relocation help" },
    { href: "/roadside-assistance", label: "Roadside Assistance", icon: Wrench, desc: "Battery, tire, fuel & lockout" },
    { href: "/laborer", label: "Laborer", icon: Users, desc: "Manual labor assistance" },
    { href: "/snow-plowing", label: "Snow Plowing", icon: Snowflake, desc: "Driveway & property clearing" },
    { href: "/lawn-mowing", label: "Lawn Mowing", icon: TreePine, desc: "Mowing, trimming & yard care" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isProfessional = user?.account_mode === "professional";

  const handleSwitchMode = async () => {
    const newMode = isProfessional ? "personal" : "professional";
    try {
      await axios.patch(`${API}/user/account-mode`, { mode: newMode }, { withCredentials: true });
      if (refreshUser) await refreshUser();
      toast.success(isProfessional ? "Switched to Personal Mode" : "Switched to Professional Mode!");
      navigate(isProfessional ? "/" : "/professional/dashboard");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to switch mode"));
    }
  };

  const personalMenuItems = [
    { to: "/client/settings", icon: Settings, label: "Settings" },
  ];

  const professionalMenuItems = [
    { to: "/professional/dashboard", icon: Briefcase, label: "Work Hub", color: "text-emerald-600 bg-emerald-100", category: "main" },
    { to: "/professional/heatmap", icon: Flame, label: "Demand Heatmap", color: "text-orange-600 bg-orange-100", category: "main" },
    { to: "/professional/earnings-insights", icon: Brain, label: "AI Insights", color: "text-violet-600 bg-violet-100", category: "main" },
    { to: "/professional/documents", icon: FolderOpen, label: "Documents", color: "text-blue-600 bg-blue-100", category: "main" },
    { to: "/wallet", icon: CreditCard, label: "Payment", color: "text-green-600 bg-green-100", category: "finance" },
    { to: "/tax", icon: Receipt, label: "Tax Info", color: "text-violet-600 bg-violet-100", category: "finance" },
    { to: "/plus-card", icon: CreditCard, label: "Plus Card", color: "text-amber-600 bg-amber-100", category: "finance" },
    { to: "/account", icon: Settings, label: "Manage Account", color: "text-stone-600 bg-stone-100", category: "settings" },
    { to: "/address", icon: MapPin, label: "Edit Address", color: "text-rose-600 bg-rose-100", category: "settings" },
    { to: "/insurance", icon: Shield, label: "Insurance", color: "text-sky-600 bg-sky-100", category: "settings" },
    { to: "/privacy", icon: Lock, label: "Privacy", color: "text-purple-600 bg-purple-100", category: "settings" },
    { to: "/settings", icon: Settings, label: "App Settings", color: "text-stone-600 bg-stone-100", category: "settings" },
    { to: "/professional/support", icon: Headphones, label: "Support Chat", color: "text-teal-600 bg-teal-100", category: "settings" },
  ];

  const menuItems = isProfessional ? professionalMenuItems : personalMenuItems;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20" data-testid="navbar">
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={location.pathname.startsWith("/admin") ? "/admin/dashboard" : isProfessional ? "/professional/dashboard" : "/"} className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_deliverstack/artifacts/2v9gvns2_EB4BFD0D-4C9D-43F2-B0D8-4B4D0DF14E28_1_201_a.jpeg" 
              alt="Stack Logo" 
              className="h-10 w-auto rounded-lg"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Services Dropdown - only for personal/non-pro users */}
            {!isProfessional && (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 font-medium text-stone-600 hover:text-emerald-950 transition-colors" data-testid="services-dropdown">
                  Services <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 p-2">
                  {services.map((service) => (
                    <DropdownMenuItem key={service.href} asChild>
                      <Link
                        to={service.href}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                          isActive(service.href) ? "bg-emerald-50 text-emerald-600" : ""
                        }`}
                      >
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <service.icon className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <div className="font-medium">{service.label}</div>
                          <div className="text-xs text-stone-500">{service.desc}</div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {!isProfessional && (
              <Link
                to="/business"
                className={`flex items-center gap-2 font-medium transition-colors ${
                  isActive("/business") ? "text-emerald-600" : "text-stone-600 hover:text-emerald-950"
                }`}
                data-testid="nav-business"
              >
                <Building2 className="w-4 h-4" />
                For Business
              </Link>
            )}
          </div>

          {/* Auth / CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {!loading && (
              isAuthenticated && user ? (
                isProfessional ? (
                  /* Professional mode: direct link to pro menu */
                  <Link
                    to="/professional/menu"
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950 text-white hover:bg-emerald-900 transition-colors"
                    data-testid="pro-menu-btn"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="font-medium">{user.name?.split(' ')[0]}</span>
                    <Menu className="w-4 h-4" />
                  </Link>
                ) : (
                  /* Personal mode: direct link to settings */
                  <Link
                    to="/client/settings"
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950 text-white hover:bg-emerald-900 transition-colors"
                    data-testid="personal-menu-btn"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="font-medium">{user.name?.split(' ')[0]}</span>
                    <Menu className="w-4 h-4" />
                  </Link>
                )
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="font-medium">Sign In</Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="btn-primary">Sign Up</Button>
                  </Link>
                </>
              )
            )}
          </div>

          {/* Mobile Menu Button - both modes go straight to their menu page */}
          {isAuthenticated && user ? (
            <Link
              to={isProfessional ? "/professional/menu" : "/client/settings"}
              className="lg:hidden p-2"
              data-testid="mobile-menu-link"
            >
              <Menu className="w-6 h-6 text-emerald-950" />
            </Link>
          ) : (
            <button
              className="lg:hidden p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          )}
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 pb-24 border-t border-stone-200 animate-fade-in max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
            {!isProfessional && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-stone-400 px-4 mb-2">SERVICES</p>
                {services.map((service) => (
                  <Link
                    key={service.href}
                    to={service.href}
                    className={`flex items-center gap-3 py-3 px-4 rounded-xl ${
                      isActive(service.href) ? "bg-emerald-50 text-emerald-600" : "text-stone-600"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <service.icon className="w-5 h-5" />
                    {service.label}
                  </Link>
                ))}
              </div>
            )}
            {!isProfessional && (
              <div className="border-t border-stone-200 pt-4">
                <Link
                  to="/business"
                  className="flex items-center gap-3 py-3 px-4 text-stone-600"
                  onClick={() => setIsOpen(false)}
                >
                  <Building2 className="w-5 h-5" />
                  For Business
                </Link>
              </div>
            )}
            <div className="pt-4 px-4 space-y-2">
              {isAuthenticated && user ? (
                <>
                  {isProfessional ? (
                    <>
                      {/* Pro mobile header */}
                      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-800 to-emerald-900 rounded-2xl text-white mb-3">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-10 h-10 rounded-full border-2 border-emerald-500" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center border-2 border-emerald-500"><User className="w-5 h-5 text-white" /></div>
                        )}
                        <div>
                          <p className="font-semibold text-sm">{user.name}</p>
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/30 text-emerald-200 px-1.5 py-0.5 rounded">Pro Mode</span>
                        </div>
                      </div>
                      {/* Dashboard & Finance */}
                      <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider px-1 pt-2">Dashboard</p>
                      {menuItems.filter(i => i.category === "main").map((item) => (
                        <Link key={item.to + item.label} to={item.to} onClick={() => setIsOpen(false)} className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-stone-50 transition-colors">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.color}`}><item.icon className="w-4 h-4" /></div>
                          <span className="font-medium text-sm text-stone-700">{item.label}</span>
                        </Link>
                      ))}
                      <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider px-1 pt-3">Finance</p>
                      {menuItems.filter(i => i.category === "finance").map((item) => (
                        <Link key={item.to + item.label} to={item.to} onClick={() => setIsOpen(false)} className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-stone-50 transition-colors">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.color}`}><item.icon className="w-4 h-4" /></div>
                          <span className="font-medium text-sm text-stone-700">{item.label}</span>
                        </Link>
                      ))}
                      <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider px-1 pt-3">Settings</p>
                      <div className="grid grid-cols-2 gap-2">
                        {menuItems.filter(i => i.category === "settings").map((item) => (
                          <Link key={item.to + item.label} to={item.to} onClick={() => setIsOpen(false)} className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors text-center">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}><item.icon className="w-4 h-4" /></div>
                            <span className="text-[11px] font-medium text-stone-600 leading-tight">{item.label}</span>
                          </Link>
                        ))}
                      </div>
                      <div className="pt-3 space-y-2">
                        <Button variant="outline" className="w-full text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => { handleSwitchMode(); setIsOpen(false); }} data-testid="mobile-switch-mode">
                          <ArrowLeftRight className="w-4 h-4 mr-2" /> Switch to Personal Mode
                        </Button>
                        <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => { handleLogout(); setIsOpen(false); }}>
                          <LogOut className="w-4 h-4 mr-2" /> Sign Out
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 py-3 px-4 bg-stone-100 rounded-xl">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <User className="w-8 h-8 p-1 bg-emerald-100 rounded-full text-emerald-600" />
                        )}
                        <div>
                          <p className="font-medium text-emerald-950">{user.name}</p>
                          <p className="text-xs text-stone-500">{user.email}</p>
                        </div>
                      </div>
                      {menuItems.map((item) => (
                        <Link key={item.to + item.label} to={item.to} onClick={() => setIsOpen(false)}>
                          <Button variant="outline" className="w-full">
                            <item.icon className="w-4 h-4 mr-2" />
                            {item.label}
                          </Button>
                        </Link>
                      ))}
                      <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => { handleLogout(); setIsOpen(false); }}>
                        <LogOut className="w-4 h-4 mr-2" /> Sign Out
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full">Sign In</Button>
                  </Link>
                  <Link to="/signup" onClick={() => setIsOpen(false)}>
                    <Button className="btn-primary w-full">Sign Up</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
